import React, { useEffect, useState } from 'react';
import Sidebar from '../Components/Sidebar';
import MobileNav from '../Components/MobileNav';
import { useIsMobile } from '../hooks/useMediaQuery';
import { supabase } from '../supabaseClient';
import Loader from '../Components/Loader';
import { useNavigate } from 'react-router-dom';

const linkifyText = (text) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex) || [];
  return parts.map((part, i) => {
    if (matches.includes(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" 
           style={{color: '#38bdf8', textDecoration: 'underline'}}
           onClick={(e) => e.stopPropagation()}>
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export default function Clans() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  
  const handleSetCollapsed = (value) => {
    setCollapsed(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebarCollapsed', value);
    }
  };

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClan, setSelectedClan] = useState(null);
  const [userClan, setUserClan] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [clanStats, setClanStats] = useState({});
  const [clans, setClans] = useState([]);
  const [filteredClans, setFilteredClans] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
      }
      
      setCurrentUser(user);

      const { data: clansData, error: clansError } = await supabase
        .from('clans')
        .select('*');

      if (clansError) {
        setError('Failed to load clans: ' + clansError.message);
        setLoading(false);
        return;
      }

      if (!clansData || clansData.length === 0) {
        setClans([]);
        setFilteredClans([]);
        setLoading(false);
        return;
      }

      const formattedClans = clansData.map(clan => ({
        name: clan.name,
        tag: clan.tag,
        image: clan.icon_url,
        about: clan.about,
        leader: clan.leader_name,
        whatsapp: clan.whatsapp_link,
        estimatedMembers: clan.member_count || 0,
        country: clan.country,
        youtube: clan.youtube,
        discord: clan.discord,
        boostless: clan.boostless
      }));
      
      setClans(formattedClans);
      setFilteredClans(formattedClans);

      if (user) {
        const { data: userData, error: userError } = await supabase
          .from('igd')
          .select('clan')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (userData) setUserClan(userData.clan);
      }

      const counts = {};
      for (const clan of clansData) {
        const { count, error: countError } = await supabase
          .from('igd')
          .select('*', { count: 'exact', head: true })
          .eq('clan', clan.name);
        
        counts[clan.name] = countError ? (clan.member_count || 0) : (count || 0);
      }
      setClanStats(counts);
      
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Something went wrong: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      const filtered = clans.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.leader.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClans(filtered);
    } else {
      setFilteredClans(clans);
    }
  }, [searchQuery, clans]);

  const ClanImage = ({ src, isUserClan }) => {
    const [error, setError] = useState(false);
    
    if (error || !src) {
      return (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? '28px' : '40px',
        }}>
          🛡️
        </div>
      );
    }

    return (
      <img
        src={src}
        alt="clan"
        onError={() => setError(true)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        background: 'var(--bg)', 
        minHeight: '100vh',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={handleSetCollapsed} />}
        <div style={{ 
          flex: 1, 
          marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'), 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: isMobile ? '20px' : '0',
        }}>
          <Loader size={isMobile ? 80 : 120} />
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        background: 'var(--bg)', 
        minHeight: '100vh',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={handleSetCollapsed} />}
        <div style={{ 
          flex: 1, 
          marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
          padding: isMobile ? '20px' : '40px',
        }}>
          <div style={{
            background: 'var(--card-bg)',
            padding: '40px',
            borderRadius: '16px',
            textAlign: 'center',
            color: 'var(--text)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3>{error}</h3>
            <button 
              onClick={loadData}
              style={{
                padding: '12px 24px',
                background: '#38bdf8',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '16px',
              }}
            >
              Retry
            </button>
          </div>
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      background: 'var(--bg)', 
      minHeight: '100vh',
      color: 'var(--text)',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={handleSetCollapsed} />}

      <div style={{ 
        flex: 1, 
        marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
        padding: isMobile ? '16px' : '40px',
        paddingBottom: isMobile ? '90px' : '40px',
        transition: 'margin-left 0.3s ease',
        overflowX: 'hidden',
      }}>
        {!selectedClan ? (
          <>
            <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
              <h1 style={{ 
                fontSize: isMobile ? '24px' : '36px', 
                fontWeight: 'bold', 
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--text)',
              }}>
                🛡️ {isMobile ? 'Clans' : 'WWMMCGC Clans'}
              </h1>
              <p style={{ color: 'var(--text-muted)', fontSize: isMobile ? '14px' : '16px' }}>
                {clans.length} elite squads • Find your battle brothers
              </p>
            </div>

            <div style={{ marginBottom: isMobile ? '16px' : '25px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={isMobile ? "Search..." : "Search clans by name, tag, or leader..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 12px 12px 44px' : '15px 20px 15px 50px',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '16px',
                    outline: 'none',
                    minHeight: '48px',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  fontSize: '18px',
                  color: 'var(--text-muted)',
                }}>
                  🔍
                </span>
              </div>
            </div>

            {clans.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: 'var(--text-muted)',
                background: 'var(--card-bg)',
                borderRadius: '16px',
              }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>📭</div>
                <h3>No clans available</h3>
                <p>Database returned empty result</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: isMobile ? '12px' : '20px',
              }}>
                {filteredClans.map((clan, index) => {
                  const isUserClan = userClan === clan.name;
                  
                  return (
                    <div
                      key={index}
                      onClick={() => setSelectedClan(clan)}
                      style={{
                        background: isUserClan ? 'rgba(34, 197, 94, 0.2)' : 'var(--card-bg)',
                        borderRadius: isMobile ? '12px' : '16px',
                        border: isUserClan ? '2px solid #22c55e' : '1px solid var(--border)',
                        padding: isMobile ? '12px' : '20px',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {isUserClan && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          padding: '4px 10px',
                          background: '#22c55e',
                          color: '#000',
                          borderRadius: '9999px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          zIndex: 10,
                        }}>
                          YOU
                        </div>
                      )}

                      <div style={{ 
                        width: '100%', 
                        paddingTop: '62.5%',
                        position: 'relative',
                        borderRadius: isMobile ? '8px' : '12px',
                        overflow: 'hidden',
                        marginBottom: isMobile ? '10px' : '12px',
                        background: '#0f172a',
                      }}>
                        <ClanImage src={clan.image} isUserClan={isUserClan} />
                      </div>

                      <h3 style={{ 
                        fontSize: isMobile ? '14px' : '16px', 
                        fontWeight: 'bold', 
                        marginBottom: '6px',
                        color: isUserClan ? '#22c55e' : 'var(--text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {clan.name}
                      </h3>

                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        background: isUserClan ? 'rgba(34, 197, 94, 0.3)' : 'rgba(56, 189, 248, 0.3)',
                        color: isUserClan ? '#22c55e' : '#38bdf8',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>
                        {clan.tag}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredClans.length === 0 && clans.length > 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: isMobile ? '40px 20px' : '60px', 
                color: 'var(--text-muted)',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                marginTop: '20px',
              }}>
                <div style={{ fontSize: isMobile ? '40px' : '48px', marginBottom: '16px' }}>🔍</div>
                <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>No clans found</h3>
                <p>Try a different search term</p>
              </div>
            )}
          </>
        ) : (
          <div>
            <button
              onClick={() => setSelectedClan(null)}
              style={{
                padding: '10px 20px',
                background: 'var(--card-bg)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                color: 'var(--text)',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
              }}
            >
              ← Back to Clans
            </button>

            <div style={{
              background: userClan === selectedClan.name ? 'rgba(34, 197, 94, 0.1)' : 'var(--card-bg)',
              borderRadius: isMobile ? '16px' : '24px',
              border: userClan === selectedClan.name ? '2px solid #22c55e' : '1px solid var(--border)',
              overflow: 'hidden',
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                padding: isMobile ? '30px 20px' : '50px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: isMobile ? '120px' : '150px',
                  height: isMobile ? '120px' : '150px',
                  margin: '0 auto 20px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: userClan === selectedClan.name ? '3px solid #22c55e' : '3px solid #38bdf8',
                  position: 'relative',
                }}>
                  <ClanImage src={selectedClan.image} isUserClan={userClan === selectedClan.name} />
                </div>
                
                {userClan === selectedClan.name && (
                  <div style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    background: '#22c55e',
                    color: '#000',
                    borderRadius: '9999px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                  }}>
                    ✓ Your Current Clan
                  </div>
                )}
                
                <h1 style={{ 
                  fontSize: isMobile ? '24px' : '32px', 
                  fontWeight: 'bold', 
                  marginBottom: '12px',
                  color: userClan === selectedClan.name ? '#22c55e' : 'var(--text)',
                }}>
                  {selectedClan.name}
                </h1>
                
                <span style={{
                  padding: '8px 16px',
                  background: userClan === selectedClan.name ? 'rgba(34, 197, 94, 0.3)' : 'rgba(56, 189, 248, 0.3)',
                  color: userClan === selectedClan.name ? '#22c55e' : '#38bdf8',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                }}>
                  {selectedClan.tag}
                </span>
              </div>

              <div style={{ padding: isMobile ? '20px' : '40px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    color: userClan === selectedClan.name ? '#22c55e' : '#38bdf8', 
                    marginBottom: '12px', 
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}>
                    About
                  </h3>
                  <p style={{ 
                    color: 'var(--text-muted)', 
                    lineHeight: '1.6', 
                    fontSize: '14px',
                  }}>
                    {linkifyText(selectedClan.about) || 'No description available.'}
                  </p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px',
                  marginBottom: '24px',
                }}>
                  <div style={{
                    background: '#0f172a',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>👥</div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: userClan === selectedClan.name ? '#22c55e' : '#38bdf8' 
                    }}>
                      {clanStats[selectedClan.name] || selectedClan.estimatedMembers || 0}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Members</div>
                  </div>

                  <div style={{
                    background: '#0f172a',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>👑</div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: '#fbbf24' 
                    }}>
                      {selectedClan.leader}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Leader</div>
                  </div>
                </div>

                <div style={{ 
                  borderTop: '1px solid var(--border)', 
                  paddingTop: '24px',
                  textAlign: 'center',
                }}>
                  <p style={{ 
                    color: 'var(--text-muted)', 
                    marginBottom: '16px', 
                    fontSize: '14px',
                  }}>
                    {userClan === selectedClan.name
                      ? 'You are a member of this clan' 
                      : 'Want to join? Connect on WhatsApp'}
                  </p>
                  
                  <a
                    href={selectedClan.whatsapp || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '14px 32px',
                      background: '#22c55e',
                      borderRadius: '10px',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      textDecoration: 'none',
                      opacity: userClan === selectedClan.name ? 0.6 : 1,
                    }}
                    onClick={(e) => {
                      if (userClan === selectedClan.name) e.preventDefault();
                    }}
                  >
                    💬 {userClan === selectedClan.name ? 'Already Joined' : 'Join Clan'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {isMobile && <MobileNav />}
    </div>
  );
}